import { useInfiniteQuery, useQuery } from "@tanstack/react-query"

import { getFairUseAccessGroupMembers, getFairUseAccessGroups } from "@/api/admin/fair-use"

export function useFairUseAccessGroupsQuery() {
  return useQuery({
    queryKey: ["admin", "fair-use", "access-groups"],
    queryFn: getFairUseAccessGroups,
    staleTime: 300_000,
    retry: false,
    refetchOnWindowFocus: false,
  })
}

export function useFairUseAccessGroupMembersQuery(groupIds: string[]) {
  const ids = [...groupIds].sort()
  return useInfiniteQuery({
    queryKey: ["admin", "fair-use", "access-group-members", ids],
    queryFn: async ({ pageParam }) => {
      const position: { index: number; cursor: string | null } = pageParam
        ? JSON.parse(pageParam)
        : { index: 0, cursor: null }
      const groupId = ids[position.index]
      let page: { member_ids: string[]; cursor: string | null }
      let error: string | undefined
      try {
        page = await getFairUseAccessGroupMembers(groupId, position.cursor ?? undefined)
      } catch (failure) {
        page = { member_ids: [], cursor: null }
        error = failure instanceof Error ? failure.message : "Group membership unavailable"
      }
      let next: string | null = null
      if (page.cursor) next = JSON.stringify({ index: position.index, cursor: page.cursor })
      else if (position.index + 1 < ids.length)
        next = JSON.stringify({ index: position.index + 1, cursor: null })
      return {
        ...page,
        group_id: groupId,
        group_complete: !error && !page.cursor,
        error,
        cursor: next,
      }
    },
    initialPageParam: null as string | null,
    getNextPageParam: (page, pages) =>
      page.cursor && pages.slice(0, -1).some((previous) => previous.cursor === page.cursor)
        ? undefined
        : page.cursor,
    enabled: ids.length > 0,
    staleTime: 300_000,
    retry: false,
    refetchOnWindowFocus: false,
  })
}
